import { api } from './api'
import type { APISession } from '@/types/session'
import { useMutation } from '@tanstack/react-query'

export interface AmendmentSummary {
    total_amendments: number
    strikeout_count: number
    underline_count: number
    pages_with_amendments: number
}

export interface AmendmentsAnalysis {
    summary: AmendmentSummary
    cost: number | string
    processed_pages: number
    s3_key?: string
}

export interface DatasourceBillVersion {
    version: number
    md5_hash: string
    created_at: string
    text_extracted: boolean
    textract_job_id?: string
    text_s3_key?: string
    pdf_s3_key?: string
    amendments_detected?: boolean
    amendments_analysis?: AmendmentsAnalysis
}

export interface DatasourceBill {
    bill_number: string
    title: string
    synopsis?: string
    session: string
    session_code: string
    crossfile_bill_number?: string
    data_source: string
    versions: DatasourceBillVersion[]
    full_text_available: boolean
    pdf_url?: string
    arrival_time?: string
    latest_version_arrival?: string
}

export interface DatasourceBillsResponse {
    items: DatasourceBill[]
    next_token?: string
}

export interface ListDatasourceBillsParams {
    data_source: string
    session_code: APISession
    limit?: number
    next_token?: string
    start_key?: string
    search_query?: string
    sort_field?: string
    sort_direction?: 'asc' | 'desc'
}


export const datasourceBillsApi = {
    listDatasourceBills: async ({
        data_source,
        session_code,
        limit = 100,
        next_token,
        start_key,
        search_query,
        sort_field,
        sort_direction
    }: ListDatasourceBillsParams): Promise<DatasourceBillsResponse> => {
        const params = new URLSearchParams()
        
        if (limit) params.append('limit', limit.toString())
        if (next_token) params.append('next_token', next_token)
        if (start_key) params.append('start_key', start_key)
        if (search_query) params.append('search_query', search_query)
        if (sort_field) params.append('sort_field', sort_field)
        if (sort_direction) params.append('sort_direction', sort_direction)

        try {
            const requestUrl = `/datasource/${encodeURIComponent(data_source)}/bills/${encodeURIComponent(session_code)}?${params.toString()}`
            console.log('[datasourceBillsApi] Making request to:', requestUrl)
            
            const response = await api.get<DatasourceBillsResponse>(requestUrl, {
                is_global: false // This is not an admin endpoint, but a direct datasource endpoint
            })
            
            console.log('[datasourceBillsApi] Response status:', response.status)
            console.log('[datasourceBillsApi] Raw response data:', response.data)
            console.log('[datasourceBillsApi] Response:', response.data.items.length, 'items')
            return response.data
        } catch (error) {
            console.error('Failed to fetch datasource bills:', error instanceof Error ? error.message : String(error))
            throw error
        }
    },

    loadAllDatasourceBills: async ({
        data_source,
        session_code,
        search_query,
        sort_field,
        sort_direction,
        on_progress
    }: Omit<ListDatasourceBillsParams, 'limit' | 'next_token' | 'start_key'> & {
        on_progress?: (currentCount: number, batch: DatasourceBill[]) => void
    }): Promise<DatasourceBill[]> => {
        console.log('[datasourceBillsApi] Starting to load all bills for:', { data_source, session_code })

        const allBills: DatasourceBill[] = []
        let nextToken: string | undefined = undefined
        let currentCount = 0
        let pageCount = 0

        try {
            do {
                pageCount++
                console.log(`[datasourceBillsApi] Loading page ${pageCount}${nextToken ? ` (token: ${nextToken.substring(0, 20)}...)` : ''}`)

                const response = await datasourceBillsApi.listDatasourceBills({
                    data_source,
                    session_code,
                    limit: 100, // Use a larger batch size for loading all
                    next_token: nextToken,
                    search_query,
                    sort_field,
                    sort_direction
                })

                if (response.items.length > 0) {
                    allBills.push(...response.items)
                    currentCount += response.items.length
                    console.log(`[datasourceBillsApi] Page ${pageCount}: +${response.items.length} bills (total: ${currentCount})`)

                    // Call progress callback if provided
                    on_progress?.(currentCount, response.items)
                } else {
                    console.log(`[datasourceBillsApi] Page ${pageCount}: No bills returned`)
                }

                nextToken = response.next_token

                // Safety check to prevent infinite loops
                if (pageCount > 1000) {
                    console.warn('[datasourceBillsApi] Reached maximum page limit (1000), stopping')
                    break
                }

            } while (nextToken)

            console.log(`[datasourceBillsApi] Completed loading all bills: ${allBills.length} total bills in ${pageCount} pages`)
            return allBills

        } catch (error: any) {
            console.error('[datasourceBillsApi] Failed to load all bills:', error.message)
            throw error
        }
    },

    getFileContent: async (s3_key: string): Promise<string> => {
        try {
            const response = await api.get<string>(`/bills/content/${s3_key}`, {
                responseType: 'text'
            })
            return response.data
        } catch (error) {
            console.error('Failed to get file content:', error)
            throw error
        }
    }
}

export const useGetDatasourceFileContent = () => {
    return useMutation({
        mutationFn: (s3_key: string) => datasourceBillsApi.getFileContent(s3_key)
    })
}
